/* eslint-disable no-magic-numbers */
import React, { CSSProperties, ReactNode, useCallback, useState } from 'react';
import clsx from 'clsx';
import { FormattedMessage, useIntl } from 'react-intl';

import { UIDragListItemTypes, toastError } from '@futrli/ui-components';
import { useDropzone } from '@futrli/shared-services';

import { ExtendedOrganisation } from '../../../../consolidation-context/types';
import { getUniqueCurrencyCodes } from '../../../../consolidation-context/utils';
import { hasDuplicateValues } from '../../../../../consolidation/consolidation-wizard-context/utils';
import { ORGS_MAX_LIMIT } from '../../../../constants';
import { OrganisationDragItem } from '../organisation-drag-item/organisation-drag-item';
import { useConsolidationContext } from '../../../../../consolidation/consolidation-context/consolidation-context';

import styles from './organisations-drag-list.scss';

export interface OrganisationsDragListProps {
  allowDrop: boolean;
  className?: string;
  isDragging?: boolean;
  isMaxOrgsLimitReached?: boolean;
  isMultiCurrency?: boolean;
  mode: 'available' | 'selected';
  noOrgsContent?: ReactNode;
  organisations: ExtendedOrganisation[];
  onClick?: (id: string) => void;
  onDragStart?: (event: React.DragEvent, id: string) => void;
  onDrop?: (event: React.DragEvent) => void;
  showColumnHeaders?: boolean;
  style?: CSSProperties;
  title: ReactNode;
  type: UIDragListItemTypes;
}

export const getTooltip = (
  isMaxOrgsLimitReached: boolean,
  wouldCauseMultiCurrencyIssue: boolean,
  orgIsPartOfEliminationGroup: boolean
) => {
  if (isMaxOrgsLimitReached) {
    return (
      <FormattedMessage
        id="consolidation.disabled_max_limit_message"
        defaultMessage="The limit for entities within a consolidation is {limit} entities."
        values={{ limit: ORGS_MAX_LIMIT }}
      />
    );
  }
  if (wouldCauseMultiCurrencyIssue) {
    return (
      <FormattedMessage
        id="consolidation.disabled_would_cause_multi_currency_message"
        defaultMessage="You are unable to add this organisation as it has a different currency and the limit for multi-currency consolidations is {limit} organisations."
        values={{ limit: ORGS_MAX_LIMIT }}
      />
    );
  }
  if (orgIsPartOfEliminationGroup) {
    return (
      <FormattedMessage
        id="consolidation.disabled_part_of_elimination_group"
        defaultMessage="This organisation cannot be removed as one or more of its accounts are part of an elimination group"
      />
    );
  }
  return null;
};

export const OrganisationsDragList = ({
  allowDrop,
  className,
  isDragging,
  isMaxOrgsLimitReached,
  isMultiCurrency,
  mode,
  noOrgsContent,
  onClick,
  onDragStart,
  onDrop,
  organisations,
  showColumnHeaders = true,
  style,
  title,
  type
}: OrganisationsDragListProps) => {
  const intl = useIntl();

  const { handleDragLeave, handleDragOver, targetted } = useDropzone(allowDrop, !!isDragging);
  const [orgIdEditingCode, setOrgIdEditingCode] = useState<string>();
  const {
    settings: { handleOrganisationCodeUpdate, orgCodeMap, orgsWithEliminations, selectedOrganisations }
  } = useConsolidationContext();
  const selectedCurrencyCodes = getUniqueCurrencyCodes(selectedOrganisations);

  const handleEditCode = useCallback((orgId: string) => {
    setOrgIdEditingCode(orgId);
  }, []);

  const handleSaveCode = useCallback(
    (value: string) => {
      if (orgIdEditingCode) {
        const newOrgCodeMap = { ...orgCodeMap, [orgIdEditingCode]: value.toUpperCase() };
        const containsDuplicateShortCodes = hasDuplicateValues(newOrgCodeMap);
        if (containsDuplicateShortCodes) {
          toastError(
            intl.formatMessage({
              id: 'consolidation.same_short_code_detected',
              defaultMessage: 'Short code needs to be unique within consolidation.'
            })
          );
        } else if (value) {
          handleOrganisationCodeUpdate(orgIdEditingCode, value.toUpperCase());
        }
        setOrgIdEditingCode(undefined);
      }
    },
    [orgCodeMap, orgIdEditingCode, intl, handleOrganisationCodeUpdate]
  );

  const maxLimitReached = mode === 'available' && isMaxOrgsLimitReached;

  const orgIsPartOfEliminationGroup = (org: ExtendedOrganisation) => {
    return mode === 'selected' && orgsWithEliminations.includes(org.id);
  };

  const wouldCauseMultiCurrencyIssue = (org: ExtendedOrganisation) => {
    return (
      mode === 'available' &&
      !isMultiCurrency &&
      !selectedCurrencyCodes.includes(org.currency_code) &&
      !!isMaxOrgsLimitReached
    );
  };

  return (
    <div className={styles.outerContainer} onDragLeave={handleDragLeave} onDragOver={handleDragOver} onDrop={onDrop}>
      {/* Had to use separate div for dropzone here as 'container' has {overflow: hidden} */}
      <div
        className={clsx(styles.dropzone, {
          [styles.targetable]: isDragging && allowDrop,
          [styles.targetted]: targetted
        })}
      />

      <div
        className={clsx(styles.container, className, { [styles.dragFlickerFix]: allowDrop && isDragging })}
        style={style}
      >
        <strong className={styles.title}>{title}</strong>
        {organisations.length === 0 ? (
          <div className={styles.noOrgsContent}>{noOrgsContent}</div>
        ) : (
          <div
            className={clsx(styles.listContainer, {
              [styles.showHeaders]: showColumnHeaders
            })}
          >
            {showColumnHeaders && (
              <div className={styles.columnTitles}>
                <div className={styles.columnTitleSpacer} />
                <div className={clsx(styles.columnTitle, styles.yearEndTitle)}>
                  <FormattedMessage id="consolidation.yr_end" defaultMessage="Yr end" />
                </div>
                <div className={clsx(styles.columnTitle, styles.currencyTitle)}>
                  <FormattedMessage id="common.currency" defaultMessage="Currency" />
                </div>
                {type !== UIDragListItemTypes.ADD && (
                  <div className={clsx(styles.columnTitle, styles.shortCodeTitle)}>
                    <FormattedMessage id="consolidation.short_code" defaultMessage="Short code" />
                  </div>
                )}
              </div>
            )}
            <ul className={styles.dragList}>
              {organisations.map(org => (
                <li key={org.id} className={styles.dragListItem}>
                  <OrganisationDragItem
                    isEditing={org.id === orgIdEditingCode}
                    isEditDisabled={!!orgIdEditingCode && org.id !== orgIdEditingCode}
                    onClick={onClick}
                    onDragStart={onDragStart}
                    onEditCode={handleEditCode}
                    onSaveCode={handleSaveCode}
                    organisation={org}
                    type={type}
                    disabled={wouldCauseMultiCurrencyIssue(org) || orgIsPartOfEliminationGroup(org) || maxLimitReached}
                    tooltip={getTooltip(
                      !!isMaxOrgsLimitReached,
                      wouldCauseMultiCurrencyIssue(org),
                      orgIsPartOfEliminationGroup(org)
                    )}
                  />
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};
